import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonLines } from '@/components/ui/skeleton';

export interface DashboardSectionProps {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Number of rows; drives the badge and the empty state. */
  count: number;
  loading?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  /** "View all" style link in the header. */
  link?: { to: string; label: string };
  /** Colour of the count badge when there is something to do. */
  tone?: 'default' | 'warning' | 'danger';
  className?: string;
  children: React.ReactNode;
}

const badgeTone: Record<NonNullable<DashboardSectionProps['tone']>, string> = {
  default: 'bg-secondary text-secondary-foreground',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  danger: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};

/** One dashboard block: heading with count badge, loading skeleton, empty state or the rows. */
export function DashboardSection({
  title,
  description,
  icon: Icon,
  count,
  loading = false,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  link,
  tone = 'default',
  className,
  children,
}: DashboardSectionProps) {
  const headingId = React.useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'flex min-w-0 flex-col rounded-lg border bg-card text-card-foreground shadow-sm',
        className,
      )}
    >
      <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
        <h2 id={headingId} className="text-sm font-semibold">
          {title}
        </h2>
        {loading ? null : (
          <Badge
            className={cn(
              'border-transparent tabular-nums shadow-none',
              badgeTone[count > 0 ? tone : 'default'],
            )}
          >
            {count}
          </Badge>
        )}
        {link ? (
          <Link
            to={link.to}
            className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            {link.label}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        ) : null}
        {description ? <p className="w-full text-xs text-muted-foreground">{description}</p> : null}
      </header>
      <div className="min-w-0 flex-1 p-3">
        {loading ? (
          <SkeletonLines lines={3} />
        ) : count === 0 ? (
          <EmptyState
            compact
            icon={emptyIcon}
            title={emptyTitle}
            description={emptyDescription}
            className="border-0 py-4"
          />
        ) : (
          <div className="scrollbar-thin overflow-x-auto">{children}</div>
        )}
      </div>
    </section>
  );
}

/** Table with the compact styling shared by the dashboard sections. */
export function SectionTable({ children, caption }: { children: React.ReactNode; caption: string }) {
  return (
    <table className="w-full text-sm">
      <caption className="sr-only">{caption}</caption>
      {children}
    </table>
  );
}

export function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {children}
      </tr>
    </thead>
  );
}

export function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-2 py-1.5 font-medium', className)}>{children}</th>;
}

export function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-2 py-2 align-middle', className)}>{children}</td>;
}

export function EntityLink({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={cn('font-medium text-foreground underline-offset-4 hover:underline', className)}>
      {children}
    </Link>
  );
}
